import React, { useState, useEffect, useCallback, useRef } from 'react';
import MissionPlanner from '../components/MissionPlanner';
import WeatherPill from '../components/map/WeatherPill';
import { DroneChip, DroneSheet, RoundButton, SearchButton, ZoomGroup } from '../components/mobile/MobileKit';
import { useNotification } from '../contexts/NotificationContext';
import { API_URL, apiFetch } from '../config/api';
import { getIconSVG } from '../utils/icons';
import { geocode } from '../utils/geo';

/**
 * Missions Page - Mission planning and management
 * Receives drones from app-level state (no independent polling)
 */
function MissionsPage({
    telemetry,
    dronesTelemetry,
    activeMissions,
    drones: propDrones,
    selectedDroneId: globalSelectedDroneId,
    onDroneSelect,
    isMobileLayout = false,
    topOffset = 0
}) {
    const [missionMapRef, setMissionMapRef] = useState(null);
    const [fallbackDrones, setFallbackDrones] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Preference: global ID (if provided by parent), then local state (for standalone/persistence)
    const [localSelectedDroneId, setLocalSelectedDroneId] = useState(() => {
        try {
            return localStorage.getItem('selectedDroneId') || null;
        } catch { return null; }
    });

    const selectedDroneId = globalSelectedDroneId !== undefined ? globalSelectedDroneId : localSelectedDroneId;
    const setSelectedDroneId = useCallback((id) => {
        if (onDroneSelect) onDroneSelect(id);
        setLocalSelectedDroneId(id);
    }, [onDroneSelect]);

    const { showToast } = useNotification();
    // Phone: waypoint editor starts collapsed so the map is visible; planner open
    // like the Flutter app's MISSION PLANNER card.
    const [bottomPanelVisible, setBottomPanelVisible] = useState(!isMobileLayout);
    const [missionPanelVisible, setMissionPanelVisible] = useState(true);
    const [droneSheetOpen, setDroneSheetOpen] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isCompact = isMobileLayout || viewportWidth <= 768;

    // Use prop drones if available, fallback to local fetch (defensive)
    const drones = propDrones !== undefined ? propDrones : fallbackDrones;
    const connectedDrones = (drones || []).filter(d => d.status === 'connected');

    // Persist selectedDroneId to localStorage
    useEffect(() => {
        try {
            if (selectedDroneId) {
                localStorage.setItem('selectedDroneId', selectedDroneId);
            } else {
                localStorage.removeItem('selectedDroneId');
            }
        } catch {}
    }, [selectedDroneId]);

    // Auto-select only when nothing is selected or the drone was deleted. A
    // drone that drops offline stays selected (shown offline): switching to
    // another one moved the planner, flight buttons and voice target to a
    // different aircraft without a prompt.
    useEffect(() => {
        if ((drones || []).some(d => d.id === selectedDroneId)) return;
        if (connectedDrones.length) setSelectedDroneId(connectedDrones[0].id);
        else if (selectedDroneId && (drones || []).length) setSelectedDroneId(null);
    }, [drones, connectedDrones, selectedDroneId, setSelectedDroneId]);

    // Fallback: only fetch drones independently if NOT receiving them from props
    useEffect(() => {
        if (propDrones !== undefined) return; // props are available, skip
        const loadDrones = async () => {
            try {
                const res = await apiFetch(`${API_URL}/drones`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.success) {
                        setFallbackDrones(data.drones || []);
                    }
                }
            } catch (error) {
                console.error('Error loading drones:', error);
            }
        };
        loadDrones();
        const interval = setInterval(loadDrones, 5000);
        return () => clearInterval(interval);
    }, [propDrones]);

    // Get the selected drone's telemetry for StatusBar
    const selectedDrone = (drones || []).find(d => d.id === selectedDroneId);
    
    // CRITICAL: Prioritize REAL-TIME WebSocket telemetry (dronesTelemetry) over STALE HTTP polling (selectedDrone.telemetry)
    // This fixes the bug where the StatusBar shows outdated data while the map is updated in real-time.
    const wsTelemetry = dronesTelemetry[selectedDroneId];
    const selectedDroneTelemetry = wsTelemetry || selectedDrone?.telemetry || telemetry || {};
    
    // Add connection status to telemetry
    const enrichedTelemetry = {
        ...selectedDroneTelemetry,
        connected: selectedDrone?.status === 'connected',
        droneName: selectedDrone?.name || 'No Drone Selected'
    };

    if (isCompact) {
        // The Flutter app's missions tab: planner card + route editor over a
        // full-bleed map, drone chip top-left, search right, zoom bottom-right.
        const tel = selectedDrone ? enrichedTelemetry : null;
        const dronePos = tel && tel.lat && tel.lon ? [tel.lat, tel.lon] : null;
        const map = () => missionMapRef?.current;
        const searchPlace = async (q) => {
            const hit = await geocode(q).catch(() => null);
            if (hit) map()?.flyTo(hit, 15);
            else showToast('No location found', 'warning');
            return !!hit;
        };
        return (
            <div className="m-missions">
                <MissionPlanner
                    selectedDrone={selectedDroneId}
                    setMapRef={setMissionMapRef}
                    bottomPanelVisible={bottomPanelVisible}
                    setBottomPanelVisible={setBottomPanelVisible}
                    missionPanelVisible={missionPanelVisible}
                    setMissionPanelVisible={setMissionPanelVisible}
                    allDrones={connectedDrones}
                    dronesTelemetry={dronesTelemetry}
                    isMobileLayout={true}
                />

                <div className="m-overlay">
                    <div className="m-weather">
                        <WeatherPill compact lat={dronePos ? dronePos[0] : 21.1458} lon={dronePos ? dronePos[1] : 79.0882} />
                    </div>
                    <div className="m-left">
                        <DroneChip drone={selectedDrone} telemetry={tel} onOpen={() => setDroneSheetOpen(true)} />
                        {!missionPanelVisible && (
                            <RoundButton icon="assignment" label="PLANNER" neon color="var(--m-cyan)" title="Mission planner" onClick={() => setMissionPanelVisible(true)} />
                        )}
                    </div>
                    <div className="m-right">
                        <SearchButton onSearch={searchPlace} />
                    </div>
                    {/* No room beside the open waypoint sheet — pinch-zoom still works */}
                    {!bottomPanelVisible && (
                        <div className="m-zoom-dock">
                            <ZoomGroup
                                onZoomIn={() => map()?.zoomIn(0.5)}
                                onZoomOut={() => map()?.zoomOut(0.5)}
                                onLocate={dronePos ? () => map()?.flyTo(dronePos, Math.max(map()?.getZoom() || 0, 17)) : null}
                            />
                        </div>
                    )}
                </div>

                {droneSheetOpen && (
                    <DroneSheet
                        title="SELECT DRONE"
                        drones={connectedDrones}
                        dronesTelemetry={dronesTelemetry}
                        selectedDroneId={selectedDroneId}
                        onSelect={(d) => setSelectedDroneId(d.id)}
                        onClose={() => setDroneSheetOpen(false)}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {/* Top app bar — floating weather pill only (no full-width bar / search / zoom) */}
            <div style={{ position: 'fixed', top: topOffset + 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1005, pointerEvents: 'none' }}>
                <WeatherPill
                    lat={enrichedTelemetry.lat && enrichedTelemetry.lat !== 0 ? enrichedTelemetry.lat : 21.1458}
                    lon={enrichedTelemetry.lon && enrichedTelemetry.lon !== 0 ? enrichedTelemetry.lon : 79.0882}
                />
            </div>

            <div style={{
                display: 'flex',
                height: '100%',
                overflow: 'hidden',
                gap: '0',
                position: 'relative',
                background: 'var(--bgPrimary)'
            }}>
                {/* Mission Planner Map */}
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', display: 'flex' }}>
                    <MissionPlanner
                        selectedDrone={selectedDroneId}
                        setMapRef={setMissionMapRef}
                        bottomPanelVisible={bottomPanelVisible}
                        setBottomPanelVisible={setBottomPanelVisible}
                        missionPanelVisible={missionPanelVisible}
                        setMissionPanelVisible={setMissionPanelVisible}
                        allDrones={connectedDrones}
                        dronesTelemetry={dronesTelemetry}
                    />
                </div>

                {/* Select Drones — floating rounded card (Stitch), no edge sidebar / toggle */}
                    {!isCompact && (
                    <aside style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 300,
                        background: 'var(--bgSecondary)',
                        border: '1px solid var(--borderSecondary)',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'visible',
                        zIndex: 1000,
                        pointerEvents: 'auto'
                    }}>
                        {/* Header */}
                        <div style={{
                            padding: '14px 16px',
                            borderBottom: '1px solid var(--borderSecondary)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0
                        }}>
                            <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem', color: 'var(--textPrimary)', fontFamily: 'var(--font-body)' }}>Select Drones</h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--accent)' }}>sensors</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', letterSpacing: 0.5 }}>Live</span>
                            </div>
                        </div>

                        {/* Dropdown Container */}
                        <div style={{ padding: '16px', position: 'relative' }} ref={dropdownRef}>
                            <label style={{
                                display: 'block',
                                fontSize: '10px',
                                fontFamily: "'JetBrains Mono', monospace",
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                color: 'var(--textMuted)',
                                marginBottom: '8px'
                            }}>
                                Active Drone
                            </label>
                            
                            {/* Dropdown Trigger */}
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: 'var(--bgTertiary)',
                                    border: dropdownOpen ? '1px solid var(--accent)' : '1px solid var(--borderSecondary)',
                                    borderRadius: '8px',
                                    padding: '10px 12px',
                                    color: 'var(--textPrimary)',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    transition: 'all 0.15s ease',
                                    boxShadow: dropdownOpen ? '0 0 10px color-mix(in srgb, var(--accent) 15%, transparent)' : 'none'
                                }}
                            >
                                {selectedDrone ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, textAlign: 'left' }}>
                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                            <div style={{
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                background: 'var(--bgPrimary)', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                                border: '1px solid var(--borderSecondary)'
                                            }}>
                                                <span dangerouslySetInnerHTML={{ __html: getIconSVG('drones', 16, 'var(--accent)') }} style={{ display: 'flex' }} />
                                            </div>
                                            <span style={{
                                                position: 'absolute', bottom: 0, right: 0,
                                                width: '8px', height: '8px', borderRadius: '50%',
                                                background: '#22c55e', border: '1.5px solid var(--bgSecondary)'
                                            }} />
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{
                                                fontWeight: 700, fontSize: '13px', color: 'var(--textPrimary)',
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                            }}>
                                                {selectedDrone.name || selectedDrone.id}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--textMuted)' }}>
                                                <span>{selectedDrone.type || 'PX4'}</span>
                                                {wsTelemetry?.battery_remaining != null && (
                                                    <>
                                                        <span>·</span>
                                                        <span className="material-symbols-outlined" style={{ fontSize: '11px', color: '#22c55e' }}>bolt</span>
                                                        <span>{Math.round(wsTelemetry.battery_remaining)}%</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <span style={{ fontSize: '13px', color: 'var(--textMuted)' }}>Select an active drone</span>
                                )}
                                <span className="material-symbols-outlined" style={{
                                    color: 'var(--textMuted)',
                                    transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.15s ease'
                                }}>
                                    expand_more
                                </span>
                            </button>

                            {/* Dropdown Options List */}
                            {dropdownOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: '16px',
                                    right: '16px',
                                    background: 'var(--bgSecondary)',
                                    border: '1px solid var(--borderSecondary)',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                    zIndex: 1010,
                                    maxHeight: '220px',
                                    overflowY: 'auto',
                                    padding: '6px 0'
                                }} className="custom-scrollbar">
                                    {connectedDrones.length === 0 ? (
                                        <div style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--textMuted)', textAlign: 'center' }}>
                                            No active drones online
                                        </div>
                                    ) : (
                                        connectedDrones.map(drone => {
                                            const isSelected = drone.id === selectedDroneId;
                                            const batt = dronesTelemetry[drone.id]?.battery_remaining;
                                            return (
                                                <div
                                                    key={drone.id}
                                                    onClick={() => {
                                                        setSelectedDroneId(drone.id);
                                                        setDropdownOpen(false);
                                                    }}
                                                    style={{
                                                        padding: '10px 14px',
                                                        background: isSelected ? 'color-mix(in srgb, var(--accent) 6%, transparent)' : 'transparent',
                                                        borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        cursor: 'pointer',
                                                        transition: 'background 0.15s ease'
                                                    }}
                                                    onMouseEnter={e => { if(!isSelected) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 3%, transparent)'; }}
                                                    onMouseLeave={e => { if(!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, textAlign: 'left' }}>
                                                        <div style={{ position: 'relative', flexShrink: 0 }}>
                                                            <div style={{
                                                                width: '24px', height: '24px', borderRadius: '50%',
                                                                background: 'var(--bgPrimary)', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center'
                                                            }}>
                                                                <span dangerouslySetInnerHTML={{ __html: getIconSVG('drones', 14, isSelected ? 'var(--accent)' : '#94a3b8') }} style={{ display: 'flex' }} />
                                                            </div>
                                                            <span style={{
                                                                position: 'absolute', bottom: 0, right: 0,
                                                                width: '6px', height: '6px', borderRadius: '50%',
                                                                background: '#22c55e'
                                                            }} />
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{
                                                                fontWeight: 600, fontSize: '12px',
                                                                color: isSelected ? 'var(--accent)' : 'var(--textPrimary)',
                                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                                            }}>
                                                                {drone.name || drone.id}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: 'var(--textMuted)' }}>
                                                        {batt != null && <span>{Math.round(batt)}%</span>}
                                                        <span style={{
                                                            padding: '2px 4px', borderRadius: '3px',
                                                            background: 'color-mix(in srgb, var(--textPrimary) 5%, transparent)', fontSize: '8px', fontWeight: 700
                                                        }}>{drone.type || 'PX4'}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    </aside>
                    )}
            </div>
        </>
    );
}

export default MissionsPage;
